/*
 *   File : preview-sales-bill.js
 *   Author : https://evoqins.com
 *   Description : Sales bill preview modal
 *   Integrations :
 *   Version : 1.0.0
*/

import { memo, useState, useEffect, useRef } from 'react'

// Custom components
import { Icon } from '../Icon';
import { SecondaryButton } from '../Buttons';
import { Loader } from '../Others';

const getFileType = (fileUrl) => {
    const url = fileUrl?.split('?')[0] || '';
    const extension = url.split('.').pop()?.toLowerCase() || '';
    const isImage = ['jpg', 'jpeg', 'png', 'webp'].includes(extension);
    const isPDF = extension === 'pdf';

    return { isImage, isPDF, extension };
};

const getDownloadFileName = (fileUrl) => {
    const { isImage, isPDF, extension } = getFileType(fileUrl);
    const path = fileUrl?.split('?')[0] || '';
    let fileName = decodeURIComponent(path.split('/').pop() || 'bill');

    if (isPDF && !fileName.toLowerCase().endsWith('.pdf')) {
        fileName = `${fileName}.pdf`;
    }

    if (isImage && !new RegExp(`\\.(${['jpg', 'jpeg', 'png', 'webp'].join('|')})$`, 'i').test(fileName)) {
        fileName = `${fileName}.${extension || 'jpg'}`;
    }

    return fileName;
};

const downloadBlob = (blob, fileName) => {
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

const PreviewSalesBillModal = (props) => {
    const [previewLoader, setPreviewLoader] = useState(true);
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
    const [previewError, setPreviewError] = useState(false);
    const [zoom, setZoom] = useState(MIN_ZOOM);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const pdfBlobUrlRef = useRef(null);
    const fileBlobRef = useRef(null);
    const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

    const { isImage, isPDF } = getFileType(props.file);

    useEffect(() => {
        setZoom(MIN_ZOOM);
        setPosition({ x: 0, y: 0 });
        setIsDragging(false);
    }, [props.file]);

    useEffect(() => {
        if (pdfBlobUrlRef.current) {
            URL.revokeObjectURL(pdfBlobUrlRef.current);
            pdfBlobUrlRef.current = null;
        }
        fileBlobRef.current = null;

        setPdfPreviewUrl(null);
        setPreviewError(false);

        if (!props.file) {
            setPreviewLoader(false);
            return undefined;
        }

        if (!isPDF) {
            setPreviewLoader(true);
            return undefined;
        }

        setPreviewLoader(true);
        let cancelled = false;

        const loadPdfPreview = async () => {
            try {
                const response = await fetch(props.file);
                if (!response.ok) {
                    throw new Error('Failed to load PDF');
                }

                const blob = await response.blob();
                const pdfBlob = blob.type === 'application/pdf'
                    ? blob
                    : new Blob([blob], { type: 'application/pdf' });
                const objectUrl = URL.createObjectURL(pdfBlob);

                if (cancelled) {
                    URL.revokeObjectURL(objectUrl);
                    return;
                }

                pdfBlobUrlRef.current = objectUrl;
                fileBlobRef.current = pdfBlob;
                setPdfPreviewUrl(objectUrl);
            } catch {
                if (!cancelled) {
                    setPreviewError(true);
                }
            } finally {
                if (!cancelled) {
                    setPreviewLoader(false);
                }
            }
        };

        loadPdfPreview();

        return () => {
            cancelled = true;
            if (pdfBlobUrlRef.current) {
                URL.revokeObjectURL(pdfBlobUrlRef.current);
                pdfBlobUrlRef.current = null;
            }
            fileBlobRef.current = null;
        };
    }, [props.file, isPDF]);

    if (!props.file) return null;

    const _handleZoomIn = () => {
        setZoom((prev) => Math.min(MAX_ZOOM, Number((prev + ZOOM_STEP).toFixed(2))));
    };

    const _handleZoomOut = () => {
        setZoom((prev) => {
            const next = Math.max(MIN_ZOOM, Number((prev - ZOOM_STEP).toFixed(2)));
            if (next === MIN_ZOOM) {
                setPosition({ x: 0, y: 0 });
            }
            return next;
        });
    };

    const _getClientPoint = (event) => {
        if (event.touches && event.touches.length > 0) {
            return { x: event.touches[0].clientX, y: event.touches[0].clientY };
        }
        return { x: event.clientX, y: event.clientY };
    };

    const _handleDragStart = (event) => {
        if (zoom <= MIN_ZOOM) return;

        event.preventDefault();
        const point = _getClientPoint(event);
        dragStartRef.current = {
            x: point.x,
            y: point.y,
            posX: position.x,
            posY: position.y,
        };
        setIsDragging(true);
    };

    const _handleDragMove = (event) => {
        if (!isDragging || zoom <= MIN_ZOOM) return;

        const point = _getClientPoint(event);
        setPosition({
            x: dragStartRef.current.posX + (point.x - dragStartRef.current.x),
            y: dragStartRef.current.posY + (point.y - dragStartRef.current.y),
        });
    };

    const _handleDragEnd = () => {
        setIsDragging(false);
    };

    const _handleDownload = async () => {
        const fileName = getDownloadFileName(props.file);

        if (fileBlobRef.current) {
            downloadBlob(fileBlobRef.current, fileName);
            return;
        }

        try {
            const response = await fetch(props.file);
            if (!response.ok) {
                throw new Error('Failed to download file');
            }

            const blob = await response.blob();
            const typedBlob = isPDF && blob.type !== 'application/pdf'
                ? new Blob([blob], { type: 'application/pdf' })
                : blob;

            downloadBlob(typedBlob, fileName);
        } catch {
            window.open(props.file, '_blank', 'noopener,noreferrer');
        }
    };

    const _renderPreview = () => {
        if (isPDF) {
            if (previewError) {
                return (
                    <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14'>
                        Unable to preview this PDF. Please use the download button.
                    </p>
                );
            }

            if (!pdfPreviewUrl) {
                return null;
            }

            return (
                <iframe
                    src={`${pdfPreviewUrl}#toolbar=0&navpanes=0`}
                    title="Scanned bill"
                    className="w-100 e-border-1px-chinese-silver e-border-radius-8"
                    style={{ height: '70vh', minHeight: 400 }}
                />
            );
        }

        if (isImage) {
            return (
                <div
                    className={`e-border-1px-chinese-silver e-border-radius-8 overflow-hidden ${previewLoader ? 'd-none' : ''}`}
                    style={{
                        maxHeight: '70vh',
                        height: '70vh',
                        cursor: zoom > MIN_ZOOM ? (isDragging ? 'grabbing' : 'grab') : 'default',
                        userSelect: 'none',
                        touchAction: 'none',
                    }}
                    onMouseDown={_handleDragStart}
                    onMouseMove={_handleDragMove}
                    onMouseUp={_handleDragEnd}
                    onMouseLeave={_handleDragEnd}
                    onTouchStart={_handleDragStart}
                    onTouchMove={_handleDragMove}
                    onTouchEnd={_handleDragEnd}
                >
                    <div
                        className='d-flex justify-content-center align-items-center h-100 w-100 p-2'
                        style={{
                            transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                            transformOrigin: 'center center',
                            transition: isDragging ? 'none' : 'transform 0.2s ease',
                        }}
                    >
                        <img
                            src={props.file}
                            alt="Scanned bill"
                            className="object-fit-contain"
                            draggable={false}
                            style={{
                                maxWidth: '100%',
                                maxHeight: 'calc(70vh - 16px)',
                                pointerEvents: 'none',
                            }}
                            onLoad={() => setPreviewLoader(false)}
                            onError={() => setPreviewLoader(false)}
                        />
                    </div>
                </div>
            );
        }

        return (
            <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14'>
                Preview not available for this file type.
            </p>
        );
    };

    return (
        <div className="modal fade"
            id="preview-sale-history-modal"
            data-bs-backdrop="static"
            data-bs-keyboard="false"
            tabIndex="-1"
            aria-labelledby="PreviewSaleHistoryModalLabel"
            aria-hidden="true">
            <div
                className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
                style={isPDF || props.width ? { maxWidth: props.width || '900px' } : undefined}
            >
                <div className={`modal-content e-border-radius-12 mx-auto pb-4 ${isPDF || props.width ? 'w-100' : 'w-400px'}`}>
                    <div className="modal-header px-4 pt-3 padding-10px-bottom border-0 align-items-center">
                        <h4 className='e-text-dark-jungle-green e-primary-font-playfair e-font-18 e-font-weight-500 line-height-28px mb-0'>
                            Scanned bill
                        </h4>
                        <div className='d-flex align-items-center gap-3'>
                            {isImage && !previewLoader &&
                                <div className='d-flex align-items-center justify-content-center'
                                    style={{ gap: 8 }}>
                                    <button
                                        type="button"
                                        className={`d-inline-flex align-items-center justify-content-center e-border-1px-indigo e-border-radius-8 e-bg-transparent e-text-indigo e-font-weight-600 ${zoom <= MIN_ZOOM ? 'opacity-50 pe-none' : 'cursor-pointer'}`}
                                        style={{ width: 32, height: 32, fontSize: 18, lineHeight: 1, padding: 0 }}
                                        onClick={_handleZoomOut}
                                        aria-label="Zoom out">
                                        <span style={{ display: 'block', lineHeight: 1, transform: 'translateY(-2px)' }}>−</span>
                                    </button>
                                    <span
                                        className='d-inline-flex align-items-center justify-content-center e-alt-font-noto-sans e-font-weight-500 e-text-raisin-black'
                                        style={{ width: 52, height: 32, fontSize: 14, lineHeight: 1 }}>
                                        {Math.round(zoom * 100)}%
                                    </span>
                                    <button
                                        type="button"
                                        className={`d-inline-flex align-items-center justify-content-center e-border-1px-indigo e-border-radius-8 e-bg-transparent e-text-indigo e-font-weight-600 ${zoom >= MAX_ZOOM ? 'opacity-50 pe-none' : 'cursor-pointer'}`}
                                        style={{ width: 32, height: 32, fontSize: 18, lineHeight: 1, padding: 0 }}
                                        onClick={_handleZoomIn}
                                        aria-label="Zoom in">
                                        <span style={{ display: 'block', lineHeight: 1, transform: 'translateY(-2px)' }}>+</span>
                                    </button>
                                </div>
                            }
                            <Icon icon="close-icon"
                                data-bs-dismiss={"modal"}
                                className='cursor-pointer'
                                size="24px"
                                onPress={props.handleClose} />
                        </div>
                    </div>
                    <div className="modal-body px-4 pb-0 pt-0 e-scroll">
                        {previewLoader && (isPDF || isImage) && (
                            <Loader height="min-vh-50" />
                        )}
                        {_renderPreview()}
                    </div>

                    {(isPDF || isImage) &&
                        <div className='modal-footer border-0 justify-content-center pb-0'>
                            <SecondaryButton name={<>
                                Download<Icon icon="download-icon" className="ms-1" size={24} />
                            </>}
                                className="e-text-indigo px-5 e-border-1px-indigo"
                                onPress={_handleDownload} />
                        </div>
                    }
                </div>
            </div>
        </div>
    )
}

PreviewSalesBillModal.defaultProps = {
    handleClose: () => { },
    width: null,
}

export default memo(PreviewSalesBillModal);
